import {
  CategoryNames,
  KitIntent,
  KitProduct,
  Product,
  RecommendedProductFamily,
} from '@omniafishing/core';
import { Modal } from 'antd';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { isDone, LoadingState } from '../../constants/loading_state';
import { useListAttributionUuid } from '../../hooks/use_list_attribution_uuid';
import { apiV1 } from '../../lib/api';
import { hashGenerator } from '../../lib/hashGenerator';
import { areKitProductsCompleteSet, isKitProduct, randomKitProductId } from '../../lib/kit';
import {
  kitCategoryNames,
  singleItemCategories,
} from '../../routes/get_started/kit_inline/kit_inline';
import Loading from '../loading/loading';
import { categoriesActiveByDefault, ClientKitProduct, DynamicKit, KitRoutes } from './dynamic_kit';
import styles from './dynamic_kit.less';

const kitIntentAsHashedString = (kitIntent: KitIntent | null) => {
  if (!kitIntent) {
    return '';
  }
  const { kit_products, season_group, species, waterbody, technique, state } = kitIntent;
  const skus = kit_products.map((kp) => kp.product.sku).sort();
  return hashGenerator(
    [season_group.name, species.name, waterbody?.id, technique.name, state, ...skus].join('-')
  );
};

interface DynamicKitModalProps {
  fishingIntent: KitIntent | null;
  open: boolean;
  onCancel: () => void;
}

export const DynamicKitModal = (props: DynamicKitModalProps) => {
  const { fishingIntent, open, onCancel } = props;

  const uuid = useListAttributionUuid();
  const [kitIntent, setKitIntent] = useState<KitIntent>();
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.NOT_STARTED);
  const [route, setRoute] = useState<KitRoutes>('products');
  const [selectedKitProduct, setSelectedKitProduct] = useState<ClientKitProduct>();
  const [selectedRecommendation, setSelectedRecommendation] = useState<RecommendedProductFamily>();
  const [selectedCategory, setSelectedCategory] = useState<CategoryNames>();

  useEffect(() => {
    setLoadingState(LoadingState.PENDING);

    let request: Promise<(KitProduct | Product)[]>;
    if (areKitProductsCompleteSet(fishingIntent.kit_products)) {
      request = Promise.resolve(fishingIntent.kit_products);
    } else if (fishingIntent.waterbody != null) {
      request = apiV1
        .waterbodyRecommendationsFetch(fishingIntent.waterbody.url_slug, {
          species: fishingIntent.species.name,
          styles: fishingIntent.technique.name,
          seasons: fishingIntent.season_group.seasons.map((sg) => sg.name),
        })
        .then((response) => {
          return [
            ...fishingIntent.kit_products, // incomplete sets of kit_products will be used first
            ..._.flatten(response.data.data.map((rf) => rf.products)),
          ];
        });
    } else {
      const fetchRecommendations = (
        page: number,
        allProducts: (KitProduct | Product)[] = []
      ): Promise<(Product | KitProduct)[]> => {
        return apiV1
          .recommendationsFetch({
            species: fishingIntent.species.name,
            styles: fishingIntent.technique.name,
            seasons: fishingIntent.season_group.seasons.map((sg) => sg.name),
            limit: 50,
            page,
          })
          .then((response) => {
            const products = [
              ...allProducts,
              ..._.flatten(response.data.data.map((rf) => rf.products)),
            ];

            if (
              areKitProductsCompleteSet(products) ||
              page === Math.ceil(response.data.metadata.total_results / 50)
            ) {
              return products;
            }

            return fetchRecommendations(page + 1, products);
          });
      };

      request = fetchRecommendations(1, fishingIntent.kit_products);
    }

    async function processRequest() {
      // fetch waterbody recommendations for each fishing intent
      // get first 1 or 2 in stock products for each category
      // mark duplicates
      // store them by technique name in Record<technique.name, RecommendedProduct[]>
      const response = await request;

      let newKitProducts: ClientKitProduct[];
      if (areKitProductsCompleteSet(fishingIntent.kit_products)) {
        // complete products were passed in, so we don't need to fetch them
        newKitProducts = fishingIntent.kit_products.map((kp, i) => ({
          id: i,
          product: kp.product,
          active: categoriesActiveByDefault.includes(kp.product.category_name),
          quantity: 1,
          duplicate: false,
        }));
      } else {
        // first product family in kit categories that has an in stock product
        const recommendedProductsFiltered = response
          .filter((p) => {
            const product = isKitProduct(p) ? p.product : p;
            return kitCategoryNames.includes(product.category_name);
          })
          .filter((p) => {
            const product = isKitProduct(p) ? p.product : p;
            return product.in_stock;
          });

        const kitProductsByCategory = kitCategoryNames.reduce((kitCategories, category) => {
          kitCategories[category] ||= [];
          return kitCategories;
        }, {} as Record<CategoryNames, ClientKitProduct[]>);

        recommendedProductsFiltered.forEach((recommendedProduct) => {
          const product = isKitProduct(recommendedProduct)
            ? recommendedProduct.product
            : recommendedProduct;
          const category = product.category_name;
          const maxLength = singleItemCategories.includes(category) ? 1 : 2;

          // only add if the category isn't full
          // only add if we don't already have this product family in the kit
          if (
            kitProductsByCategory[category].length < maxLength &&
            !kitProductsByCategory[category].some(
              (kp) => kp.product.shopify_product_id === product.shopify_product_id
            )
          ) {
            const newKitProduct = {
              id: randomKitProductId(),
              product,
              active: categoriesActiveByDefault.includes(category),
              quantity: 1,
              duplicate: false,
            };
            kitProductsByCategory[category].push(newKitProduct);
          }
        });

        newKitProducts = _.flatten(Object.values(kitProductsByCategory));
      }

      setKitIntent({
        ...fishingIntent,
        kit_products: newKitProducts,
      });
      setLoadingState(LoadingState.DONE);
    }

    processRequest();
  }, [fishingIntent]);

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      width={950}
      footer={null}
      className={styles.modal}
      afterClose={() => {
        setRoute('products');
        setSelectedKitProduct(undefined);
        setSelectedRecommendation(undefined);
        setSelectedCategory(undefined);
      }}
    >
      {isDone(loadingState) ? (
        <DynamicKit
          onCancel={onCancel}
          list_uuid={uuid}
          loadingState={loadingState}
          kitIntent={kitIntent}
          key={kitIntentAsHashedString(kitIntent)}
          route={route}
          selectedKitProduct={selectedKitProduct}
          selectedRecommendation={selectedRecommendation}
          selectedCategory={selectedCategory}
          onRouteChange={setRoute}
          onSelectedCategoryChange={setSelectedCategory}
          onSelectedKitProductChange={setSelectedKitProduct}
          onSelectedRecommendationChange={setSelectedRecommendation}
        />
      ) : (
        <Loading text="Assembling Your Kit" />
      )}
    </Modal>
  );
};
